import axios from 'axios';
import { toast } from 'react-toastify';

export const API_URL =
    process.env.NODE_ENV === 'production'
        ? `https://letsroll.site/api`
        : `http://localhost:5000/api`;

const $api = axios.create({
    withCredentials: true,
    baseURL: API_URL,
});

// SEC #8: token lives only in memory — sessionStorage is readable by any XSS script on the same origin
let _studentToken: string | null = null;
export const setStudentToken = (token: string | null) => { _studentToken = token; };
export const hasStudentToken = () => !!_studentToken;

$api.interceptors.request.use((config) => {
    if (_studentToken) {
        config.headers.Authorization = `Bearer ${_studentToken}`;
    }
    return config;
});

let isRefreshing = false;
// @ts-ignore
let failedQueue = [];

// @ts-ignore
const processQueue = (error, token = null) => {
    // @ts-ignore
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const handleUnauthorized = async () => {
    // Check if user is a teacher or student before clearing storage
    const userStr = sessionStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const isTeacher = user.roles === 'TEACHER' || sessionStorage.getItem('tokenteacher');

    // Try to delete token from database
    try {
        const token = sessionStorage.getItem('token');
        if (token) {
            await axios.post(`${API_URL}/v1/logout`, {}, {
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: true
            });
        }
    } catch (e) {
        // Ignore errors from logout call
        console.log('Logout cleanup completed');
    }

    // Clear all auth data
    setStudentToken(null); // SEC #8: clear in-memory token
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('isAuthUser');
    sessionStorage.clear();

    // Redirect to appropriate login page
    const loginPage = isTeacher ? '/instructorLogin' : '/login';
    setTimeout(() => {
        window.location.href = loginPage;
    }, 2900);
};

$api.interceptors.response.use(
    response => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 401 && !originalRequest._isRetry) {
            if (isRefreshing) {
                // If token is already being refreshed, queue the original request
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        return $api.request(originalRequest);
                    })
                    .catch(err => {
                        return Promise.reject(err); // Propagate error if queue processing fails
                    });
            }

            originalRequest._isRetry = true;
            isRefreshing = true;

            try {
                const userStr = sessionStorage.getItem('user');
                const user = userStr ? JSON.parse(userStr) : {};
                const isTeacher = user.roles === 'TEACHER' || !!sessionStorage.getItem('tokenteacher');

                const { data } = isTeacher
                    ? await axios.get(`${API_URL}/v1/teacher/refresh`, { withCredentials: true })
                    : await axios.post(`${API_URL}/v1/refresh`, {}, { withCredentials: true });
                setStudentToken(data.accessToken); // SEC #8: store in memory only, not sessionStorage
                $api.defaults.headers.common['Authorization'] = 'Bearer ' + data.accessToken;
                originalRequest.headers['Authorization'] = 'Bearer ' + data.accessToken;
                processQueue(null, data.accessToken); // Process queued requests with new token
                return $api.request(originalRequest); // Retry the original request
            } catch (e) {
                processQueue(e, null); // Process queued requests with error
                handleUnauthorized();
                toast.info('Du har blivit utloggad pga. lång inaktivitet, vänligen logga in igen.');
                return Promise.reject(e); // Important to reject the promise on refresh failure
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error); // For any other errors or if it's already a retry
    }
);

export default $api;